/**
 * Assignments & Tests — every question set in the system.
 *
 * The other listings answer scoped questions: what is public, what did I make,
 * what was I given. This one answers "what exists", which is the question an
 * operator has to answer before they can act on anything — whose paper is this,
 * has anyone sat it, does it declare a marking rule, did it produce a report.
 *
 * Two tabs over one query. A test and an assignment are the same row with a
 * different `type`, and keeping them in one page rather than two means the
 * filters, the columns and the drill-in are written once.
 *
 * See Backend/functions/src/admin/content/adminQuestionSetSearch.ts.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DataTable, Badge, TabStrip, type ColumnDef, type SortState } from '@shared-ui/components';
import { adminApi, type AdminQuestionSetListRow } from '../../api/adminApi';
import { ByStudent } from './ByStudent';
import './AssignmentsAndTests.css';

/** Model spend, to the cent when it is dollars, finer when it is not. */
const formatUsd = (v: number) => `$${v >= 1 ? v.toFixed(2) : v.toFixed(4)}`;

type TabKey = 'test' | 'practice-set' | 'by-student';

const PAGE_SIZE = 25;

/** A filter the server understands, held as the empty string when unset so the
 *  `<select>` elements stay controlled. */
interface Filters {
  status: string;
  visibility: string;
  subject: string;
  exam: string;
  class: string;
  owner: string;
  tenant: string;
  marking: string;   // '' | 'yes' | 'no'
  search: string;
}

const EMPTY: Filters = { status: '', visibility: '', subject: '', exam: '', class: '', owner: '', tenant: '', marking: '', search: '' };

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function AssignmentsAndTests() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // The tab lives in the URL so a link to "the assignments view" is a link
  // somebody can send, which is most of the point of an entry point.
  const tabParam = params.get('tab');
  const tab: TabKey = tabParam === 'practice-set' || tabParam === 'by-student' ? tabParam : 'test';
  const isPaperList = tab !== 'by-student';
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ sortBy: 'updated', sortOrder: 'desc' });

  const setTab = (next: TabKey) => {
    setParams(next === 'test' ? {} : { tab: next });
    setPage(1);
  };

  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);   // a narrowed list starts at its own beginning
  };

  // The filter options come from the corpus, not from a constant. A hardcoded
  // list goes stale in both directions: it offers a subject nobody has authored
  // and omits one added last week.
  const { data: facets } = useQuery({
    queryKey: ['admin-question-set-facets', tab],
    queryFn: () => adminApi.getQuestionSetFacets(tab as 'test' | 'practice-set'),
    enabled: isPaperList,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-question-sets', tab, filters, page, sort],
    queryFn: () => adminApi.searchQuestionSets({
      type: tab as 'test' | 'practice-set',
      ...(filters.status ? { status: filters.status as 'draft' | 'published' } : {}),
      ...(filters.visibility ? { visibility: filters.visibility as 'public' | 'private' } : {}),
      ...(filters.subject ? { subject: filters.subject } : {}),
      ...(filters.exam ? { exam: filters.exam } : {}),
      ...(filters.class ? { class: filters.class } : {}),
      ...(filters.owner ? { ownerId: filters.owner } : {}),
      ...(filters.tenant ? { tenantId: filters.tenant } : {}),
      ...(filters.marking ? { hasMarkingScheme: filters.marking === 'yes' } : {}),
      ...(filters.search ? { search: filters.search } : {}),
      page,
      pageSize: PAGE_SIZE,
      sortBy: sort.sortBy,
      sortDir: sort.sortOrder,
    }),
    enabled: isPaperList,
    // Holds the previous page's rows while the next loads, so paging does not
    // collapse the table to a skeleton on every click.
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  const columns = useMemo<ColumnDef<AdminQuestionSetListRow>[]>(() => {
    const isTest = tab === 'test';
    return [
      {
        key: 'title',
        header: 'Title',
        sortable: true,
        render: r => (
          <div>
            <div className="aat-title">{r.title}</div>
            <div className="aat-sub">
              {r.subjects.map(capitalize).join(', ') || 'No subject'}
              {' · '}{r.totalQuestions} question{r.totalQuestions === 1 ? '' : 's'}
            </div>
          </div>
        ),
      },
      {
        key: 'owner',
        header: 'Owner',
        // Orders by the id rather than the display name — the name is resolved
        // per page and Cosmos has never seen it — so this groups one person's
        // papers rather than alphabetising them.
        sortable: true,
        render: r => (
          <div>
            <div>{r.ownerName ?? <span className="aat-mono">{r.createdBy.slice(0, 8)}</span>}</div>
            <div className="aat-sub">{r.visibility}</div>
          </div>
        ),
      },
      {
        key: 'tenant',
        header: 'Tenant',
        render: r => r.tenantName ?? (r.tenantId ? <span className="aat-mono">{r.tenantId}</span> : <span className="aat-sub">—</span>),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: r => (
          <Badge variant={r.status === 'published' ? 'success' : 'default'} size="sm">{r.status}</Badge>
        ),
      },
      // Tests only. An assignment carries no marking rule by design — the
      // session engine drops one if it is there — so a column of dashes would
      // read as a gap rather than as "not applicable here".
      ...(isTest ? [{
        key: 'marking',
        header: 'Scheme',
        render: (r: AdminQuestionSetListRow) => (r.hasMarkingScheme
          ? <Badge variant="success" size="sm">yes</Badge>
          : <Badge variant="warning" size="sm">none</Badge>),
      }] : []),
      {
        key: 'assignments',
        header: 'Given to',
        render: r => <span className="aat-num">{r.assignments || '—'}</span>,
      },
      {
        key: 'sittings',
        header: 'Sat',
        render: r => <span className="aat-num">{r.sittings || '—'}</span>,
      },
      // Reports are a test artifact. On the assignments tab the column would be
      // structurally empty.
      ...(isTest ? [{
        key: 'reports',
        header: 'Reports',
        render: (r: AdminQuestionSetListRow) => <span className="aat-num">{r.reports || '—'}</span>,
      }] : []),
      {
        // Model spend: importing the paper plus every attempt at it.
        key: 'cost',
        header: 'LLM cost',
        render: r => r.llmCostUsd === undefined ? <span className="aat-sub">—</span> : (
          <span title={r.llmCost ? `Import ${formatUsd(r.llmCost.importUsd)} · attempts ${formatUsd(r.llmCost.attemptsUsd)}` : undefined}>
            {formatUsd(r.llmCostUsd)}
          </span>
        ),
      },
      {
        key: 'created',
        header: 'Created',
        sortable: true,
        render: r => (r.createdAt ? format(new Date(r.createdAt), 'MMM d, yyyy') : '—'),
      },
    ];
  }, [tab]);

  return (
    <div className="aat-page">
      <div className="aat-head">
        <h1 className="aat-h1">Assignments &amp; Tests</h1>
      </div>

      <TabStrip
        ariaLabel="Assignments and tests"
        active={tab}
        onChange={k => setTab(k as TabKey)}
        tabs={[
          { key: 'test', label: 'Tests' },
          { key: 'practice-set', label: 'Assignments' },
          { key: 'by-student', label: 'By Student' },
        ]}
      />

      {tab === 'by-student' && <ByStudent />}

      {isPaperList && (
        <div className="aat-filters">
          <select value={filters.status} onChange={e => set('status', e.target.value)} aria-label="Status">
            <option value="">Any status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <select value={filters.visibility} onChange={e => set('visibility', e.target.value)} aria-label="Visibility">
            <option value="">Any visibility</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
          <select value={filters.owner} onChange={e => set('owner', e.target.value)} aria-label="Teacher">
            <option value="">Any teacher</option>
            {(facets?.owners ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <select value={filters.tenant} onChange={e => set('tenant', e.target.value)} aria-label="Tenant">
            <option value="">Any tenant</option>
            {(facets?.tenants ?? []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={filters.subject} onChange={e => set('subject', e.target.value)} aria-label="Subject">
            <option value="">Any subject</option>
            {(facets?.subjects ?? []).map(x => <option key={x} value={x}>{capitalize(x)}</option>)}
          </select>
          {/* Exam and grade are withheld when nothing in the corpus declares one
              — an always-empty dropdown is a filter that cannot work, shown. */}
          {!!facets?.exams.length && (
            <select value={filters.exam} onChange={e => set('exam', e.target.value)} aria-label="Exam">
              <option value="">Any exam</option>
              {facets.exams.map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          )}
          {!!facets?.classes.length && (
            <select value={filters.class} onChange={e => set('class', e.target.value)} aria-label="Grade">
              <option value="">Any grade</option>
              {facets.classes.map(x => <option key={x} value={x}>Class {x}</option>)}
            </select>
          )}
          {tab === 'test' && (
            <select value={filters.marking} onChange={e => set('marking', e.target.value)} aria-label="Marking scheme">
              <option value="">Any scheme</option>
              <option value="yes">Has a marking scheme</option>
              <option value="no">No marking scheme</option>
            </select>
          )}
          {Object.values(filters).some(Boolean) && (
            <button className="btn-link" onClick={() => { setFilters(EMPTY); setPage(1); }}>Clear</button>
          )}
        </div>
      )}

      {isPaperList && error && <div className="aat-error">{error instanceof Error ? error.message : 'Could not load the list.'}</div>}

      {isPaperList && <DataTable<AdminQuestionSetListRow>
        columns={columns}
        data={rows}
        rowKey={r => r.id}
        loading={isLoading}
        compact
        onSearch={q => { set('search', q); }}
        searchPlaceholder="Search by title…"
        sort={sort}
        onSortChange={s => { setSort(s); setPage(1); }}
        pagination={{
          page,
          totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
          total,
          onPageChange: setPage,
        }}
        emptyMessage={tab === 'test' ? 'No tests match these filters.' : 'No assignments match these filters.'}
        onRowClick={r => navigate(`/assignments-tests/${r.id}`)}
        rowHref={r => `/assignments-tests/${r.id}`}
        rowTestId="question-set-row"
      />}
    </div>
  );
}
